if (!customElements.get('rr-product-panel')) {
  customElements.define(
    'rr-product-panel',
    class RrProductPanel extends HTMLElement {
      connectedCallback() {
        this.variants = JSON.parse(this.querySelector('[data-rr-variants]').textContent);
        this.fieldsets = [...this.querySelectorAll('.rr-panel__option')];
        this.colorFieldset = this.fieldsets.find((fieldset) => fieldset.querySelector('.rr-panel__value--swatch'));
        this.idInput = this.querySelector('input[name="id"]');
        this.button = this.querySelector('.rr-panel__cta');

        this.onClick = this.onClick.bind(this);
        this.onStateChange = this.updateButton.bind(this);
        this.addEventListener('click', this.onClick);
        document.addEventListener('rr:drop-state', this.onStateChange);

        this.setupLightbox();
        this.setupSwipe();
        this.update();
      }

      // Swipe left/right on the main picture (touch only): next/previous picture, looping at both ends.
      // Only horizontal swipes count, so scrolling the page over the picture still works.
      setupSwipe() {
        const main = this.querySelector('.rr-panel__main');
        if (!main) return;
        main.addEventListener(
          'touchstart',
          (event) => {
            this.swipeX = event.touches[0].clientX;
            this.swipeY = event.touches[0].clientY;
          },
          { passive: true }
        );
        main.addEventListener('touchend', (event) => {
          const dx = event.changedTouches[0].clientX - this.swipeX;
          const dy = event.changedTouches[0].clientY - this.swipeY;
          if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
          this.stepMedia(dx < 0 ? 1 : -1);
        });
      }

      // Steps through the pictures currently offered as thumbnails (i.e. those of the selected colour).
      stepMedia(direction) {
        const thumbs = [...this.querySelectorAll('.rr-panel__thumb:not([hidden])')];
        if (thumbs.length < 2) return;
        const current = thumbs.findIndex((thumb) => thumb.hasAttribute('aria-current'));
        const next = thumbs[(Math.max(current, 0) + direction + thumbs.length) % thumbs.length];
        this.showMedia(next.dataset.mediaId);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('rr:drop-state', this.onStateChange);
        if (this.lightbox?.open) this.lightbox.close();
      }

      // Lightbox: every image of every colour. Escape is handled here (on keyup, like the drop tiles) and stopped,
      // so closing the lightbox doesn't also close the drop panel around it.
      setupLightbox() {
        this.lightbox = this.querySelector('[data-rr-lightbox]');
        if (!this.lightbox) return;
        this.lightboxFigures = [...this.lightbox.querySelectorAll('.rr-lightbox__media')];

        this.lightbox.addEventListener('cancel', (event) => event.preventDefault());
        this.lightbox.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.stepLightbox(-1);
          if (event.key === 'ArrowRight') this.stepLightbox(1);
          if (event.key === 'Escape') event.preventDefault();
        });
        this.lightbox.addEventListener('keyup', (event) => {
          if (event.key !== 'Escape') return;
          event.stopPropagation();
          this.lightbox.close();
        });
        this.lightbox.addEventListener('touchstart', (event) => (this.touchX = event.touches[0].clientX), {
          passive: true,
        });
        this.lightbox.addEventListener('touchend', (event) => {
          const delta = event.changedTouches[0].clientX - this.touchX;
          if (Math.abs(delta) > 50) this.stepLightbox(delta < 0 ? 1 : -1);
        });
      }

      openLightbox(mediaId) {
        const index = this.lightboxFigures.findIndex((figure) => figure.dataset.mediaId === mediaId);
        this.showLightboxFigure(Math.max(index, 0));
        if (!this.lightbox.open) this.lightbox.showModal();
      }

      stepLightbox(direction) {
        const total = this.lightboxFigures.length;
        this.showLightboxFigure((this.lightboxIndex + direction + total) % total);
      }

      showLightboxFigure(index) {
        this.lightboxIndex = index;
        this.lightboxFigures.forEach((figure, i) => (figure.hidden = i !== index));
        const pad = (n) => String(n).padStart(2, '0');
        this.lightbox.querySelector('[data-rr-lightbox-count]').textContent = `${pad(index + 1)} / ${pad(
          this.lightboxFigures.length
        )}`;
        this.lightbox.querySelector('[data-rr-lightbox-label]').textContent = this.lightboxFigures[index].dataset.label;
      }

      onLightboxClick(event) {
        if (event.target.closest('[data-rr-lightbox-prev]')) return this.stepLightbox(-1);
        if (event.target.closest('[data-rr-lightbox-next]')) return this.stepLightbox(1);
        // Close button, or a click on the empty area around the picture.
        const stage = this.lightbox.querySelector('[data-rr-lightbox-stage]');
        if (
          event.target.closest('[data-rr-lightbox-close]') ||
          event.target === this.lightbox ||
          event.target === stage ||
          event.target.classList.contains('rr-lightbox__media')
        ) {
          this.lightbox.close();
        }
      }

      onClick(event) {
        if (this.lightbox?.contains(event.target)) return this.onLightboxClick(event);

        const slide = event.target.closest('[data-rr-lightbox-open]');
        if (slide && this.lightbox) return this.openLightbox(slide.dataset.mediaId);

        const value = event.target.closest('.rr-panel__value');
        if (value) {
          value
            .closest('.rr-panel__values')
            .querySelectorAll('.rr-panel__value')
            .forEach((button) => button.setAttribute('aria-pressed', button === value ? 'true' : 'false'));
          this.update(value.closest('.rr-panel__option') === this.colorFieldset);
          this.updateUrl();
          return;
        }

        const thumb = event.target.closest('.rr-panel__thumb');
        if (thumb) this.showMedia(thumb.dataset.mediaId);
      }

      get selectedValues() {
        return this.fieldsets.map((fieldset) => fieldset.querySelector('[aria-pressed="true"]')?.dataset.value);
      }

      update(colorChanged = false) {
        const selected = this.selectedValues;
        this.variant = this.fieldsets.length
          ? this.variants.find((variant) => variant.options.every((option, index) => option === selected[index]))
          : this.variants[0];

        this.fieldsets.forEach((fieldset, index) => {
          const label = fieldset.querySelector('[data-rr-option-value]');
          if (label) label.textContent = selected[index] || '';
        });

        if (this.variant) {
          this.idInput.value = this.variant.id;
          this.querySelectorAll('[data-rr-price]').forEach((node) => (node.textContent = this.variant.price));
        }

        this.filterMediaByColor();
        if (colorChanged || this.variant?.media) {
          const firstVisible = this.querySelector('.rr-panel__thumb:not([hidden])');
          const mediaId = this.variant?.media || firstVisible?.dataset.mediaId;
          if (mediaId) this.showMedia(String(mediaId));
        }

        this.updateButton();
      }

      // Images whose alt text matches the selected colour belong to that colour; others are always shown.
      filterMediaByColor() {
        const color = this.colorFieldset?.querySelector('[aria-pressed="true"]')?.dataset.color;
        const thumbs = [...this.querySelectorAll('.rr-panel__thumb')];
        const colorHandles = [...(this.colorFieldset?.querySelectorAll('.rr-panel__value') || [])].map(
          (button) => button.dataset.color
        );
        thumbs.forEach((thumb) => {
          const belongsToColor = colorHandles.includes(thumb.dataset.color);
          thumb.hidden = Boolean(color) && belongsToColor && thumb.dataset.color !== color;
        });
      }

      showMedia(mediaId) {
        if (!this.querySelector(`.rr-panel__slide[data-media-id="${mediaId}"]`)) return;
        this.querySelectorAll('.rr-panel__slide').forEach((slide) => (slide.hidden = slide.dataset.mediaId !== mediaId));
        this.querySelectorAll('.rr-panel__thumb').forEach((thumb) =>
          thumb.toggleAttribute('aria-current', thumb.dataset.mediaId === mediaId)
        );
      }

      updateUrl() {
        if (!this.hasAttribute('data-update-url') || !this.variant) return;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', this.variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      updateButton() {
        if (this.hasAttribute('data-sealed')) {
          this.button.disabled = true;
          return;
        }
        const available = Boolean(this.variant?.available);
        const open = this.hasAttribute('data-always-open') || document.documentElement.dataset.dropState === 'live';
        this.button.disabled = !(available && open);
        this.querySelector('[data-rr-cta-available]').hidden = !available;
        this.querySelector('[data-rr-cta-soldout]').hidden = available;
      }
    }
  );
}
